import { v4 as uuidv4 } from "uuid";
import type { Tables } from "@/lib/supabase/supabaseTypes";
import type { WorkoutExercise, WorkoutSet } from "@/lib/states/workout/store";

export type SessionExerciseRow = Pick<
  Tables<"exercises">,
  | "id"
  | "name"
  | "instructions"
  | "tracking_type"
  | "primary_muscles"
  | "equipment"
  | "experience"
> &
  Pick<
    Tables<"workout_exercises">,
    "ex_order" | "sub_order" | "sets" | "tempo" | "rir" | "rest_time" | "coach_note"
  >;

const getNumeric = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const getString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

export const createDefaultSet = (
  trackingType?: string | null
): WorkoutSet => {
  if (trackingType === "timed") {
    return { id: uuidv4() };
  }
  if (trackingType?.startsWith("distance")) {
    return { id: uuidv4() };
  }
  return { id: uuidv4(), startRange: 10, endRange: 12 };
};

const normalizeTemplateSet = (
  set: unknown,
  trackingType?: string | null
): WorkoutSet => {
  if (!set || typeof set !== "object") {
    return createDefaultSet(trackingType);
  }
  const typed = set as Record<string, unknown>;
  return {
    id: uuidv4(),
    startRange:
      getNumeric(typed.startRange) ??
      getNumeric(typed.start_range) ??
      undefined,
    endRange:
      getNumeric(typed.endRange) ??
      getNumeric(typed.end_range) ??
      undefined,
    targetWeight:
      getNumeric(typed.targetWeight) ??
      getNumeric(typed.target_weight) ??
      undefined,
    targetTimeSeconds:
      getNumeric(typed.targetTimeSeconds) ??
      getNumeric(typed.target_time_seconds) ??
      undefined,
    targetDistanceMeters:
      getNumeric(typed.targetDistanceMeters) ??
      getNumeric(typed.target_distance_meters) ??
      undefined,
  };
};

export const buildWorkoutSessionExercises = (
  rows: SessionExerciseRow[]
): WorkoutExercise[][] => {
  const sorted = [...rows].sort(
    (a, b) => (a.ex_order ?? 0) - (b.ex_order ?? 0)
  );
  const grouped: WorkoutExercise[][] = [];

  sorted.forEach((row) => {
    const trackingType = row.tracking_type ?? "strength";
    const templateSets = Array.isArray(row.sets) ? row.sets : [];
    const sets =
      templateSets.length > 0
        ? templateSets.map((set) => normalizeTemplateSet(set, trackingType))
        : [createDefaultSet(trackingType)];

    const exercise: WorkoutExercise = {
      id: row.id,
      ex_order: row.ex_order ?? 0,
      sub_order: row.sub_order ?? 0,
      name: row.name,
      tracking_type: row.tracking_type,
      tempo: getString(row.tempo) ?? undefined,
      rest_time: getNumeric(row.rest_time) ?? undefined,
      rir: getNumeric(row.rir) ?? undefined,
      coach_note: getString(row.coach_note) ?? undefined,
      instructions: row.instructions ?? undefined,
      primary_muscles: row.primary_muscles ?? undefined,
      equipment: row.equipment ?? undefined,
      experience: row.experience ?? undefined,
      sets,
      notes: null,
    };

    const order = exercise.ex_order ?? 0;
    if (!grouped[order]) {
      grouped[order] = [exercise];
    } else {
      grouped[order].push(exercise);
      grouped[order].sort(
        (a, b) => (a.sub_order ?? 0) - (b.sub_order ?? 0)
      );
    }
  });

  return grouped;
};
